import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Globe,
  Server,
  Compass,
  Users,
  FileText,
  Search,
  CheckCircle,
  Rocket,
  GraduationCap,
  Code,
  BookOpen,
  UserCheck,
  DollarSign,
  ShieldCheck,
  Building2,
  Mail,
  ExternalLink,
  ArrowRight,
  ChevronRight,
} from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0 },
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};

export default function HomePage() {
  return (
    <div>
      {/* Hero Section */}
      <section
        data-testid="hero-section"
        className="relative bg-white overflow-hidden"
      >
        <div className="absolute inset-0 bg-[#F9FAFB] opacity-50" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32 lg:py-40">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="max-w-3xl"
          >
            <motion.div variants={fadeUp} transition={{ duration: 0.5 }}>
              <Badge
                variant="secondary"
                className="mb-6 text-[#0056D2] bg-[#EFF6FF] border-[#0056D2]/10 px-3 py-1 text-xs font-medium"
              >
                Nonprofit Organization
              </Badge>
            </motion.div>

            <motion.h1
              variants={fadeUp}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-[#111827] tracking-tight leading-tight"
            >
              GlitchRealm Foundation
            </motion.h1>

            <motion.p
              variants={fadeUp}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="mt-6 text-lg md:text-xl text-[#4B5563] leading-relaxed max-w-2xl"
            >
              Empowering young developers by providing domain grants, hosting
              support, and mentorship to help students launch real projects and
              build the skills that shape their future.
            </motion.p>

            <motion.div
              variants={fadeUp}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="mt-8 flex flex-col sm:flex-row gap-3"
            >
              <Link to="/apply">
                <Button
                  data-testid="hero-apply-btn"
                  size="lg"
                  className="bg-[#0056D2] hover:bg-[#0044A5] text-white rounded-md px-8 h-12 text-base"
                >
                  Apply for Support
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Button
                data-testid="hero-learn-btn"
                variant="outline"
                size="lg"
                className="rounded-md px-8 h-12 text-base border-[#E5E7EB] text-[#111827] hover:bg-[#F3F4F6]"
                onClick={() =>
                  document
                    .getElementById("programs")
                    ?.scrollIntoView({ behavior: "smooth" })
                }
              >
                Learn About the Program
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Mission Section */}
      <section
        id="mission"
        data-testid="mission-section"
        className="bg-white py-16 md:py-24"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={staggerContainer}
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center"
          >
            <motion.div variants={fadeUp} transition={{ duration: 0.5 }}>
              <p className="text-[#0056D2] font-medium text-sm uppercase tracking-wider mb-3">
                Our Mission
              </p>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#111827] tracking-tight">
                Building the next generation of web creators
              </h2>
              <p className="mt-5 text-[#4B5563] text-base md:text-lg leading-relaxed">
                The GlitchRealm Foundation exists to support students who are
                learning web development and help them bring their ideas to life
                online. We believe every young developer deserves the opportunity
                to launch a real project, gain practical experience, and build
                confidence in their technical skills.
              </p>
              <p className="mt-4 text-[#4B5563] text-base md:text-lg leading-relaxed">
                Through domain grants, hosting assistance, and project guidance,
                we remove the barriers that prevent students from turning their
                classroom learning into portfolio-worthy work.
              </p>
            </motion.div>
            <motion.div
              variants={fadeUp}
              transition={{ duration: 0.5, delay: 0.15 }}
            >
              <div className="bg-[#F9FAFB] rounded-lg p-8 md:p-10 border border-[#E5E7EB]">
                <div className="space-y-6">
                  {[
                    {
                      icon: GraduationCap,
                      title: "Education First",
                      desc: "Supporting students in learning practical web development skills",
                    },
                    {
                      icon: Rocket,
                      title: "Launch Real Projects",
                      desc: "Helping students publish and share their work with the world",
                    },
                    {
                      icon: Users,
                      title: "Build Community",
                      desc: "Connecting young developers with resources and mentorship",
                    },
                  ].map((item) => (
                    <div key={item.title} className="flex gap-4">
                      <div className="flex-shrink-0 w-10 h-10 bg-[#EFF6FF] rounded-lg flex items-center justify-center">
                        <item.icon className="w-5 h-5 text-[#0056D2]" />
                      </div>
                      <div>
                        <h3 className="font-heading font-semibold text-[#111827] text-base">
                          {item.title}
                        </h3>
                        <p className="text-sm text-[#4B5563] mt-0.5">
                          {item.desc}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Programs Section */}
      <section
        id="programs"
        data-testid="programs-section"
        className="bg-[#F9FAFB] py-16 md:py-24"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={staggerContainer}
            className="text-center mb-12 md:mb-16"
          >
            <motion.p
              variants={fadeUp}
              className="text-[#0056D2] font-medium text-sm uppercase tracking-wider mb-3"
            >
              What We Provide
            </motion.p>
            <motion.h2
              variants={fadeUp}
              className="font-heading text-3xl md:text-4xl font-semibold text-[#111827] tracking-tight"
            >
              Programs &amp; Resources
            </motion.h2>
            <motion.p
              variants={fadeUp}
              className="mt-4 text-[#4B5563] text-base md:text-lg max-w-2xl mx-auto"
            >
              We offer a range of support services designed to help young
              developers take their first steps into the web.
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={staggerContainer}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {[
              {
                icon: Globe,
                title: "Domain Grants",
                desc: "Free domain registration for qualifying student projects, giving your work a professional online presence.",
              },
              {
                icon: Server,
                title: "Hosting Support",
                desc: "Basic hosting assistance so students can deploy their projects and make them accessible to the world.",
              },
              {
                icon: Compass,
                title: "Project Guidance",
                desc: "Mentorship and technical advice to help students plan, build, and refine their web projects.",
              },
              {
                icon: BookOpen,
                title: "Community Resources",
                desc: "Access to learning materials, coding resources, and a supportive community of fellow student developers.",
              },
            ].map((program) => (
              <motion.div
                key={program.title}
                variants={fadeUp}
                transition={{ duration: 0.4 }}
              >
                <Card
                  data-testid={`program-card-${program.title.toLowerCase().replace(/\s+/g, '-')}`}
                  className="h-full border-[#E5E7EB] bg-white hover:shadow-md transition-shadow duration-200"
                >
                  <CardHeader>
                    <div className="w-12 h-12 bg-[#EFF6FF] rounded-lg flex items-center justify-center mb-3">
                      <program.icon className="w-6 h-6 text-[#0056D2]" />
                    </div>
                    <CardTitle className="font-heading text-lg text-[#111827]">
                      {program.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-[#4B5563] leading-relaxed">
                      {program.desc}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Eligibility Section */}
      <section
        id="eligibility"
        data-testid="eligibility-section"
        className="bg-white py-16 md:py-24"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={staggerContainer}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <motion.div variants={fadeUp} transition={{ duration: 0.5 }}>
                <p className="text-[#0056D2] font-medium text-sm uppercase tracking-wider mb-3">
                  Eligibility
                </p>
                <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#111827] tracking-tight">
                  Who Can Apply
                </h2>
                <p className="mt-5 text-[#4B5563] text-base md:text-lg leading-relaxed">
                  Our programs are open to young people who are passionate about
                  web development and eager to learn. We welcome applications from
                  a variety of backgrounds and skill levels. Whether you're just starting your coding journey or building your first real project, you belong here.
                </p>
                <p className="mt-4 text-[#4B5563] text-base md:text-lg leading-relaxed">
                  <strong>The basic requirement:</strong> You must be between 12 and 18 years old and working on a web-based project. We support all types of student developers—from classroom assignments to independent passion projects to team collaborations. We believe every young developer deserves the opportunity to take their learning from theory to practice.
                </p>
              </motion.div>

              <motion.div
                variants={fadeUp}
                transition={{ duration: 0.5, delay: 0.15 }}
                className="space-y-4"
              >
                {[
                  {
                    icon: UserCheck,
                    title: "Students Ages 12 to 18",
                    desc: "Open to middle school and high school students with an interest in technology. Any student currently enrolled in school or homeschooled is welcome to apply.",
                  },
                  {
                    icon: Code,
                    title: "Beginner to Intermediate Developers",
                    desc: "No advanced experience required. We support learners at every stage, from complete beginners to students with moderate coding experience. Whether you're just learning HTML/CSS or building with JavaScript frameworks, we've got you covered.",
                  },
                  {
                    icon: Users,
                    title: "School Coding Clubs & Teams",
                    desc: "Groups and clubs working on collaborative coding projects can apply together. Teacher-led programs, computer science clubs, robotics teams, and school competitions are all welcome.",
                  },
                  {
                    icon: Rocket,
                    title: "Independent Student Projects",
                    desc: "Students working on personal projects outside of school are welcome. Hackathon projects, passion projects, startup ideas, or anything you're building to learn—we support it.",
                  },
                  {
                    icon: ShieldCheck,
                    title: "Project Requirements",
                    desc: "Your project should be a web-based or web-related initiative. It can be a portfolio site, web app, game, learning project, or community tool. The key is that you're actively learning and building.",
                  },
                  {
                    icon: DollarSign,
                    title: "Financial Need Not Required",
                    desc: "We don't require proof of financial need. Our grants are available to all qualifying students regardless of background or income level. We simply want to support young developers pursuing their passion.",
                  },
                ].map((item) => (
                  <div
                    key={item.title}
                    data-testid={`eligibility-${item.title.toLowerCase().replace(/\s+/g, '-')}`}
                    className="flex gap-4 p-4 rounded-lg border border-[#E5E7EB] bg-white hover:border-[#0056D2]/20 transition-colors"
                  >
                    <div className="flex-shrink-0 w-10 h-10 bg-[#EFF6FF] rounded-lg flex items-center justify-center">
                      <item.icon className="w-5 h-5 text-[#0056D2]" />
                    </div>
                    <div>
                      <h3 className="font-heading font-semibold text-[#111827] text-base">
                        {item.title}
                      </h3>
                      <p className="text-sm text-[#4B5563] mt-0.5">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* How It Works */}
      <section
        id="how-it-works"
        data-testid="how-it-works-section"
        className="bg-[#F9FAFB] py-16 md:py-24"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={staggerContainer}
          >
            <div className="text-center mb-12 md:mb-16">
              <motion.p
                variants={fadeUp}
                className="text-[#0056D2] font-medium text-sm uppercase tracking-wider mb-3"
              >
                Process
              </motion.p>
              <motion.h2
                variants={fadeUp}
                className="font-heading text-3xl md:text-4xl font-semibold text-[#111827] tracking-tight"
              >
                How It Works
              </motion.h2>
              <motion.p
                variants={fadeUp}
                className="mt-4 text-[#4B5563] text-base md:text-lg max-w-2xl mx-auto"
              >
                Our application process is straightforward and designed to be
                accessible for students of all experience levels.
              </motion.p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
              {[
                {
                  step: "01",
                  icon: FileText,
                  title: "Submit Application",
                  desc: "Fill out our simple online application with details about yourself and your project.",
                },
                {
                  step: "02",
                  icon: Search,
                  title: "Project Review",
                  desc: "Our team reviews your application and assesses your project's goals and feasibility.",
                },
                {
                  step: "03",
                  icon: CheckCircle,
                  title: "Approval",
                  desc: "Approved applicants are notified and matched with the appropriate support.",
                },
                {
                  step: "04",
                  icon: Rocket,
                  title: "Support Provided",
                  desc: "Receive your domain grant, hosting setup, or other resources to launch your project.",
                },
              ].map((item) => (
                <motion.div
                  key={item.step}
                  variants={fadeUp}
                  transition={{ duration: 0.4 }}
                  data-testid={`step-${item.step}`}
                  className="text-center"
                >
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#0056D2] text-white font-heading font-bold text-lg mb-5">
                    {item.step}
                  </div>
                  <div className="w-12 h-12 mx-auto bg-[#EFF6FF] rounded-lg flex items-center justify-center mb-4">
                    <item.icon className="w-6 h-6 text-[#0056D2]" />
                  </div>
                  <h3 className="font-heading font-semibold text-[#111827] text-lg mb-2">
                    {item.title}
                  </h3>
                  <p className="text-sm text-[#4B5563] leading-relaxed max-w-xs mx-auto">
                    {item.desc}
                  </p>
                </motion.div>
              ))}
            </div>

            <motion.div
              variants={fadeUp}
              className="text-center mt-12"
            >
              <Link to="/apply">
                <Button
                  data-testid="how-it-works-apply-btn"
                  className="bg-[#0056D2] hover:bg-[#0044A5] text-white rounded-md px-8 h-12 text-base"
                >
                  Start Your Application
                  <ChevronRight className="ml-1 w-4 h-4" />
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Transparency Section */}
      <section
        id="transparency"
        data-testid="transparency-section"
        className="bg-white py-16 md:py-24"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={staggerContainer}
          >
            <div className="max-w-3xl mx-auto text-center mb-12">
              <motion.p
                variants={fadeUp}
                className="text-[#0056D2] font-medium text-sm uppercase tracking-wider mb-3"
              >
                Transparency
              </motion.p>
              <motion.h2
                variants={fadeUp}
                className="font-heading text-3xl md:text-4xl font-semibold text-[#111827] tracking-tight"
              >
                How Funds Are Used
              </motion.h2>
              <motion.p
                variants={fadeUp}
                className="mt-4 text-[#4B5563] text-base md:text-lg"
              >
                We are committed to transparency and accountability. Here is how
                funding supports our mission.
              </motion.p>
            </div>

            <motion.div
              variants={fadeUp}
              className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto"
            >
              {[
                {
                  icon: Globe,
                  title: "Domain Registrations",
                  desc: "Funding covers the cost of registering custom domains for approved student projects.",
                  color: "bg-[#EFF6FF]",
                },
                {
                  icon: Server,
                  title: "Hosting Services",
                  desc: "Supporting reliable hosting infrastructure so student projects stay online and accessible.",
                  color: "bg-[#F0FDF4]",
                },
                {
                  icon: Building2,
                  title: "Operations & Infrastructure",
                  desc: "Covering essential operational costs including platform maintenance and administration.",
                  color: "bg-[#FFF7ED]",
                },
              ].map((item) => (
                <Card
                  key={item.title}
                  data-testid={`transparency-card-${item.title.toLowerCase().replace(/\s+/g, '-')}`}
                  className="text-center border-[#E5E7EB] bg-white"
                >
                  <CardContent className="pt-8 pb-6 px-6">
                    <div
                      className={`w-14 h-14 ${item.color} rounded-lg flex items-center justify-center mx-auto mb-4`}
                    >
                      <item.icon className="w-7 h-7 text-[#0056D2]" />
                    </div>
                    <h3 className="font-heading font-semibold text-[#111827] text-lg mb-2">
                      {item.title}
                    </h3>
                    <p className="text-sm text-[#4B5563] leading-relaxed">
                      {item.desc}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </motion.div>

            <motion.div variants={fadeUp} className="mt-10 text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F3F4F6] border border-[#E5E7EB]">
                <ShieldCheck className="w-4 h-4 text-[#0056D2]" />
                <span className="text-sm text-[#4B5563]">
                  100% of program funding goes directly to student support
                </span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section
        id="contact"
        data-testid="contact-section"
        className="bg-[#F9FAFB] py-16 md:py-24"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={staggerContainer}
            className="max-w-2xl mx-auto text-center"
          >
            <motion.p
              variants={fadeUp}
              className="text-[#0056D2] font-medium text-sm uppercase tracking-wider mb-3"
            >
              Get In Touch
            </motion.p>
            <motion.h2
              variants={fadeUp}
              className="font-heading text-3xl md:text-4xl font-semibold text-[#111827] tracking-tight"
            >
              Contact Us
            </motion.h2>
            <motion.p
              variants={fadeUp}
              className="mt-4 text-[#4B5563] text-base md:text-lg leading-relaxed"
            >
              Have questions about our programs or want to learn more? We would
              love to hear from you.
            </motion.p>

            <motion.div
              variants={fadeUp}
              className="mt-8 space-y-4"
            >
              <a
                href="mailto:contact@glitchrealm.com"
                data-testid="contact-email-link"
                className="flex items-center justify-center gap-3 p-4 rounded-lg border border-[#E5E7EB] bg-white hover:border-[#0056D2]/20 transition-colors"
              >
                <Mail className="w-5 h-5 text-[#0056D2]" />
                <span className="text-[#111827] font-medium">
                  contact@glitchrealm.com
                </span>
              </a>

              <a
                href="https://glitchrealm.ca"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-website-link"
                className="flex items-center justify-center gap-3 p-4 rounded-lg border border-[#E5E7EB] bg-white hover:border-[#0056D2]/20 transition-colors"
              >
                <ExternalLink className="w-5 h-5 text-[#0056D2]" />
                <span className="text-[#111827] font-medium">
                  glitchrealm.ca
                </span>
              </a>
            </motion.div>

            <motion.div variants={fadeUp} className="mt-8">
              <Link to="/apply">
                <Button
                  data-testid="contact-apply-btn"
                  className="bg-[#0056D2] hover:bg-[#0044A5] text-white rounded-md px-8 h-12 text-base"
                >
                  Apply for Support
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
